import { useEffect } from 'react';
import { useAuiState } from '@assistant-ui/react';
import { useChatInterfaceLocale } from '@/lib/chat-i18n';
import { useOptionalWorkspace } from '@/workspace/context';
import { isActionPointer } from './action-confirmation';
import { activityTrace } from './activity-trace';
import { answerCitations } from './activity-summary';
import { publishToolTiming, publishTurnActivity } from './activity-store';

/** Restore Activity from every saved message, including standalone action
 * receipts that intentionally have no visible ToolActivity row. */
export function MessageActivity({ toolIndices }: { toolIndices: readonly number[] }) {
  const messageParts = useAuiState((state) => state.message.parts);
  const messageId = useAuiState((state) => state.message.id);
  const threadId = useOptionalWorkspace()?.threadId;
  const locale = useChatInterfaceLocale();
  const tools = toolIndices.flatMap((index) => {
    const part = messageParts[index];
    return part?.type === 'tool-call' ? [part] : [];
  });
  const includedToolIds = new Set(tools.map((part) => part.toolCallId));
  const traceParts = messageParts.flatMap((part) =>
    part.type === 'reasoning' ||
    (part.type === 'tool-call' &&
      (includedToolIds.has(part.toolCallId) || isActionPointer(part.result)))
      ? [part]
      : [],
  );
  const sourceIds = messageParts.flatMap((part) =>
    part.type === 'source' && typeof part.id === 'string' ? [part.id] : [],
  );
  const trace = activityTrace(
    messageId,
    traceParts,
    sourceIds,
    locale,
    answerCitations(messageParts),
  );
  const fingerprint = JSON.stringify(trace);
  useEffect(() => {
    if (threadId && trace.steps.length) publishTurnActivity(threadId, messageId, trace);
  }, [threadId, messageId, fingerprint]);
  useEffect(() => {
    if (!threadId || typeof performance === 'undefined') return;
    for (const part of tools) {
      publishToolTiming(threadId, messageId, part.toolCallId, part.status?.type === 'running');
    }
  }, [
    threadId,
    messageId,
    tools.map((part) => `${part.toolCallId}:${part.status?.type}`).join('|'),
  ]);
  return null;
}
